import { tt } from '@agos/shared'

/**
 * The category tabs on the beverage board, and what they actually match.
 *
 * Beverage categories are free text — the franchise uses *Nápoje nealko*, *Nápoje
 * alko*, *Víno* and *Pivo* — so legacy sorts a row into a tab by looking for
 * substrings in its category **and** its name, diacritics stripped. That heuristic is
 * kept: with the 37 beverage rows in the restored data every one lands in exactly one
 * of the four *category* tabs. *Čapované* is a facet that crosses them — see
 * `EXCLUSIVE_TABS`.
 *
 * One of the six tabs never matched anything. **FB-079.**
 */

export type BeverageTab = 'all' | 'nealko' | 'alko' | 'pivo' | 'vino' | 'capovane'

export interface BeverageRow {
  stockItemId?: number
  stockName?: string
  unit?: string
  category?: string
  receipt?: number
  sales?: number
  stockQty?: number
}

export const BEVERAGE_TABS: readonly { key: BeverageTab; label: string }[] = [
  { key: 'all', label: tt('Všetky') },
  { key: 'nealko', label: 'Nealko' },
  { key: 'alko', label: 'Alko' },
  { key: 'pivo', label: tt('Pivo') },
  { key: 'vino', label: tt('Víno') },
  { key: 'capovane', label: tt('Čapované') },
] as const

/**
 * Lower case with the diacritics removed, so *Víno* and *vino* are the same word.
 *
 * The tab **keys** are ASCII for the same reason legacy's are not: legacy compares
 * `tab === tt("víno")`, putting a key through the translator. In Slovak `tt` returns
 * its input so it works by accident; in any other language the comparison is against
 * a translated string and the tab stops matching. A key is not user-visible text.
 */
export function normalise(text: string | undefined): string {
  return (text ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Mn}/gu, '')
}

/**
 * Whether a row belongs to a tab.
 *
 * **FB-079.** Legacy's draught test is `combined.includes("capovane")` — the *neuter*
 * ending. Slovak adjectives agree with their noun, and both draught products in the
 * data are something else: *Kofola čapovan**á*** and *Krušovice čapovan**ý***. So the
 * tab matched nothing at all. Matching the stem `capovan` covers every ending.
 */
export function matchesTab(row: BeverageRow, tab: BeverageTab): boolean {
  if (tab === 'all') return true

  const haystack = `${normalise(row.category)} ${normalise(row.stockName)}`

  if (tab === 'capovane') return haystack.includes('capovan')
  if (tab === 'nealko') return haystack.includes('nealko')
  if (tab === 'alko') {
    return (haystack.includes('alko') && !haystack.includes('nealko')) || haystack.includes('liehoviny')
  }
  if (tab === 'pivo') return haystack.includes('pivo')

  // Wine last, and deliberately narrow: a row that already reads as beer or as
  // (n)alcoholic is not sorted here as well.
  return (
    (haystack.includes('vino') || haystack.includes('wine')) &&
    !haystack.includes('alko') &&
    !haystack.includes('pivo')
  )
}

export function filterRows(
  rows: BeverageRow[],
  tab: BeverageTab,
  search: string,
): BeverageRow[] {
  const needle = normalise(search.trim())
  return rows.filter((row) => {
    if (!matchesTab(row, tab)) return false
    if (!needle) return true
    return (
      normalise(row.stockName).includes(needle) || normalise(row.category).includes(needle)
    )
  })
}

/** How many rows each tab holds, so a tab that matches nothing is visible. */
export function tabCounts(rows: BeverageRow[]): Record<BeverageTab, number> {
  const counts = {} as Record<BeverageTab, number>
  for (const tab of BEVERAGE_TABS) {
    counts[tab.key] = rows.filter((row) => matchesTab(row, tab.key)).length
  }
  return counts
}

/**
 * The four tabs that are meant to partition the list.
 *
 * *Čapované* is not one of them: it cuts across the others, because how a drink is
 * dispensed is orthogonal to what kind of drink it is. Kofola čapovaná is genuinely
 * both non-alcoholic and draught, and Krušovice čapovaný is both beer and draught —
 * so once FB-079 is fixed those rows appear in two tabs, correctly. The tab counts
 * therefore do not add up to the total, and were never meant to.
 */
const EXCLUSIVE_TABS: readonly BeverageTab[] = ['nealko', 'alko', 'pivo', 'vino'] as const

/**
 * Rows the category tabs cannot place, and rows two of them both claim.
 *
 * Measured over the four partitioning tabs only — see `EXCLUSIVE_TABS`. The heuristic
 * is a guess over free text and can do both; saying so beats a board that quietly
 * leaves a drink out of every tab. All 37 rows in the restored data land in exactly
 * one, and nothing stops a new category from breaking that.
 */
export function tabCoverage(rows: BeverageRow[]): { unplaced: number; duplicated: number } {
  let unplaced = 0
  let duplicated = 0

  for (const row of rows) {
    const hits = EXCLUSIVE_TABS.filter((tab) => matchesTab(row, tab)).length
    if (hits === 0) unplaced += 1
    if (hits > 1) duplicated += 1
  }
  return { unplaced, duplicated }
}

export type StockBadge = { label: string; color: 'red' | 'zinc' | 'amber' | 'green' }

/** Legacy's bands. Below zero is its own state, not just "low". */
export function stockBadge(qty: number | undefined): StockBadge {
  const quantity = qty ?? 0
  if (quantity < 0) return { label: tt('Záporný'), color: 'red' }
  if (quantity === 0) return { label: tt('Nulový'), color: 'zinc' }
  if (quantity <= 5) return { label: tt('Nízky'), color: 'amber' }
  return { label: 'OK', color: 'green' }
}

/**
 * A quantity with its unit — `1,24 l`, `0 l`, `2 ks`.
 *
 * Three decimals at most and no trailing zeros, because a beverage is poured in
 * fractions of a litre and `1,240 l` is noise. Legacy's `toFixed(3).replace(/\.?0+$/)`
 * strips a trailing zero from an integer too — `100` becomes `1` — which never fired
 * only because it guards with `Number.isInteger` first.
 */
export function formatQty(qty: number | undefined, unit: string | undefined): string {
  const quantity = qty ?? 0
  const rounded = Math.round(quantity * 1000) / 1000
  const text = Number.isInteger(rounded)
    ? String(rounded)
    : String(rounded).replace('.', ',')
  return `${text} ${unit ?? ''}`.trim()
}

export interface BeverageTotals {
  rows: number
  receiptTotal: number
  salesTotal: number
  negative: number
  empty: number
}

/**
 * Counted per row, not summed across units.
 *
 * Litres and pieces do not add up to anything, so the totals are counts — how many
 * drinks moved, how many are out, how many are below zero — rather than a single
 * meaningless number.
 */
export function beverageTotals(rows: BeverageRow[]): BeverageTotals {
  return {
    rows: rows.length,
    receiptTotal: rows.filter((row) => (row.receipt ?? 0) > 0).length,
    salesTotal: rows.filter((row) => (row.sales ?? 0) > 0).length,
    negative: rows.filter((row) => (row.stockQty ?? 0) < 0).length,
    empty: rows.filter((row) => (row.stockQty ?? 0) === 0).length,
  }
}
